import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { Checkbox } from '@/components/checkbox';
import {
  CursorValue,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  FONTS,
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { gql, useMutation } from '@apollo/client';
import { isOptionDisabled } from './utils';
import { Props as ListItemProps } from '@/components/list-item';
import { ORDER_INBOUND_ASSIGNED_EMPLOYEE_FIELDS_FRAGMENT } from './queries';
import {
  ORDER_INBOUND_LIST_ITEM_HEIGHT,
  OrderInboundsList,
  useSharedOrderListStyles,
} from './order-inbounds-list';
import { OrderInboundOption, OrderInboundSection } from './types';
import { partnerSupplier } from '@/utils/environment';
import { RecyclerListRef } from '@/components/recycler-list';
import {
  SmartOrder_MergeMutation,
  SmartOrder_MergeMutationVariables,
} from '@/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';

export const MERGE_ORDER_INBOUNDS = gql`
  mutation SmartOrder_Merge(
    $businessOrganizationUUID: uuid!
    $smartOrdersToUpdate: [uuid!]!
    $mergeUUID: uuid!
    $assignedEmployeeUUID: uuid
    $updatedAt: timestamptz!
    $updatedBy: uuid!
  ) {
    update_smart_order(
      where: {
        supplier: {
          business_organization_uuid: { _eq: $businessOrganizationUUID }
        }
        uuid: { _in: $smartOrdersToUpdate }
      }
      _set: {
        merge_uuid: $mergeUUID
        assigned_employee_uuid: $assignedEmployeeUUID
        updated_at: $updatedAt
        updated_by_uuid: $updatedBy
      }
    ) {
      returning {
        merge_uuid
        uuid
        updated_at
        assigned_employee {
          ...OrderInboundAssignedEmployeeFields
        }
      }
    }
  }

  ${ORDER_INBOUND_ASSIGNED_EMPLOYEE_FIELDS_FRAGMENT}
`;

interface SmartOrderToUpdate {
  assignedEmployeeUUID?: string;
  orderUUID: string;
}

export interface UseMergeOrderInboundsMutationInput {
  businessOrganizationUUID: string;
  mergeUUID: string;
  smartOrdersToUpdate: SmartOrderToUpdate[];
  updatedAt: string;
  updatedBy: string;
}

export const useMergeOrderInboundsMutation = () => {
  const [mergeOrderInbounds, mergeOrderInboundsStatus] = useMutation<
    SmartOrder_MergeMutation,
    SmartOrder_MergeMutationVariables
  >(MERGE_ORDER_INBOUNDS);

  const mergeOrders = useCallback(
    ({
      businessOrganizationUUID,
      mergeUUID,
      smartOrdersToUpdate,
      updatedAt,
      updatedBy,
    }: UseMergeOrderInboundsMutationInput) => {
      const smartOrderUUIDs = smartOrdersToUpdate.map(
        (order) => order.orderUUID,
      );

      const uniqueAssignedEmployees = Array.from(
        new Set(
          smartOrdersToUpdate
            .map((order) => order.assignedEmployeeUUID)
            .filter(Boolean),
        ),
      );

      // If we only have one unique employee across all the smart orders, then we assign the employee to all of the
      // orders. Otherwise, we set all the orders to unassigned.
      const assignedEmployeeUUID: string | null =
        uniqueAssignedEmployees.length === 1
          ? uniqueAssignedEmployees[0]!
          : null;

      return mergeOrderInbounds({
        variables: {
          assignedEmployeeUUID,
          businessOrganizationUUID,
          mergeUUID,
          smartOrdersToUpdate: smartOrderUUIDs,
          updatedAt,
          updatedBy,
        },
      });
    },
    [mergeOrderInbounds],
  );

  return [mergeOrders, mergeOrderInboundsStatus] as const;
};

interface Props {
  initialSelectedOrderUUID?: string;
  onClose: () => void;
  onMerge: (mergedOrders: string[], newMergeUUID: string) => void;
  orderInboundSection: OrderInboundSection;
}

const MODAL_CHROME_HEIGHT = 238;

export const MergeOrderInboundsActionSheet: React.FC<Props> = ({
  initialSelectedOrderUUID,
  onClose,
  orderInboundSection,
  onMerge,
}) => {
  const { user } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const [selectedOrders, setSelectedOrders] = useState<
    Record<string | number, OrderInboundOption | null>
  >(() => {
    if (initialSelectedOrderUUID) {
      const initialOrder = orderInboundSection.data.find(
        (order) => order.value === initialSelectedOrderUUID,
      );
      if (initialOrder && !isOptionDisabled(initialOrder)) {
        return { [initialOrder.value]: initialOrder };
      }
    }
    return {};
  });
  const [smartOrdersToUpdate, setSmartOrdersToUpdate] = useState<
    SmartOrderToUpdate[]
  >(() => {
    if (initialSelectedOrderUUID) {
      const initialOrder = orderInboundSection.data.find(
        (order) => order.value === initialSelectedOrderUUID,
      );

      if (initialOrder?.metadata && !isOptionDisabled(initialOrder)) {
        return initialOrder.metadata.orderUUIDs.map((orderUUID) => ({
          assignedEmployeeUUID: initialOrder.metadata?.assignedEmployee?.uuid,
          orderUUID,
        }));
      }
    }
    return [];
  });
  const listRef = useRef<RecyclerListRef<OrderInboundOption>>(null);

  const [sharedStyles] = useSharedOrderListStyles();

  const [mergeOrderInbounds, { loading }] = useMergeOrderInboundsMutation();

  const { isSmallLayout } = useLayoutContext();
  const windowDimensions = useWindowDimensions();
  const orderListHeight =
    orderInboundSection.data.length * ORDER_INBOUND_LIST_ITEM_HEIGHT;
  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.9);
  const actionSheetMinHeight = Math.min(
    actionSheetMaxHeight,
    orderListHeight + MODAL_CHROME_HEIGHT,
  );

  const onSelect = useCallback((option: OrderInboundOption) => {
    if (isOptionDisabled(option)) {
      return;
    }

    setSelectedOrders((current) => {
      const newSelection = {
        ...current,
        [option.value]: current[option.value] ? null : option,
      };

      const newOrderIds = Object.values(newSelection).reduce<
        SmartOrderToUpdate[]
      >((orders, order) => {
        if (order) {
          if (order.metadata?.mergeUUID) {
            order.metadata.orderUUIDs.forEach((orderUUID) => {
              orders.push({
                assignedEmployeeUUID: order.metadata?.assignedEmployee?.uuid,
                orderUUID,
              });
            });
          } else {
            orders.push({
              assignedEmployeeUUID: order.metadata?.assignedEmployee?.uuid,
              orderUUID: String(order.value),
            });
          }
        }
        return orders;
      }, []);

      setSmartOrdersToUpdate(newOrderIds);

      return newSelection;
    });
  }, []);

  const listItemProps: Partial<ListItemProps<OrderInboundOption>> = useMemo(
    () => ({
      leftComponentStyle: styles.leftComponentStyle,
      listItemStyle: (option) =>
        isOptionDisabled(option) ? styles.disabledListItem : null,
      renderLeftComponent: (option) => {
        const id = option.value;
        const isSelected = Boolean(selectedOrders[option.value]);
        const isDisabled = isOptionDisabled(option);
        return (
          <View testID={`order-inbound-${id}-checkbox`}>
            <Checkbox
              disabled={isDisabled}
              isSelected={isSelected}
              onSelect={() => onSelect(option)}
              style={styles.checkbox}
              trackingData={{
                name: `merge_order_inbounds.checkbox`,
                properties: {
                  checked: !isSelected, // event sent before the incoming state change
                  title: option.title,
                },
              }}
            />
          </View>
        );
      },
    }),
    [onSelect, selectedOrders],
  );

  return (
    <BaseActionSheet
      contentStyle={[
        styles.content,
        {
          maxHeight: actionSheetMaxHeight,
          minHeight: actionSheetMinHeight,
        },
      ]}
      headerContent={
        <Text
          style={[
            FONTS.primarySemiBold,
            TYPOGRAPHY.h2,
            styles.title,
            isSmallLayout ? styles.titleSmall : null,
          ]}
        >
          {i18n.t('merge_inbounds')}
        </Text>
      }
      isVisible
      modalHeaderStyle={styles.modalHeader}
      onClose={onClose}
      testID="merge-order-inbound-action-sheet"
    >
      <>
        <View style={styles.listContainer}>
          <OrderInboundsList
            isMergeList
            listItemProps={listItemProps}
            listRef={listRef}
            onSelect={onSelect}
            renderSectionHeader={(item) => {
              const { isFirstSection } = item.metadata || {};
              const headerStyle = [
                sharedStyles.sectionHeader,
                isFirstSection ? sharedStyles.sectionHeaderFirst : null,
              ];
              return (
                <View key={item.title} style={headerStyle}>
                  <Text style={[FONTS.primarySemiBold, sharedStyles.subTitle]}>
                    {item.title}
                  </Text>
                </View>
              );
            }}
            sections={[orderInboundSection]}
          />
        </View>
        <View style={styles.buttonContainer}>
          <Button
            disabled={smartOrdersToUpdate.length < 2}
            loading={loading}
            onPress={async () => {
              try {
                const { data } = await mergeOrderInbounds({
                  businessOrganizationUUID:
                    partnerSupplier.businessOrganizationUUID,
                  mergeUUID: uuidv4(),
                  smartOrdersToUpdate,
                  updatedAt: new Date().toISOString(),
                  updatedBy: user.uuid,
                });
                if (data?.update_smart_order?.returning[0]?.merge_uuid) {
                  onMerge(
                    smartOrdersToUpdate.map((order) => order.orderUUID),
                    data.update_smart_order.returning[0].merge_uuid,
                  );
                }
              } catch (error) {
                Analytics.track('merge_order_inbounds.error', { error });
                showErrorToast();
              }
              onClose();
            }}
            testID="merge-order-inbound-button"
            trackingData={{
              name: 'merge_order_inbounds_button.press',
              properties: {
                smartOrderIDs: smartOrdersToUpdate,
              },
            }}
          >
            {i18n.t('merge')}
          </Button>
        </View>
      </>
    </BaseActionSheet>
  );
};

const TITLE_PADDING = 8;

const styles = StyleSheet.create({
  buttonContainer: {
    marginTop: SCREEN_PADDING,
  },
  checkbox: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  content: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  disabledListItem: {
    cursor: 'not-allowed' as CursorValue,
    opacity: 0.5,
  },
  leftComponentStyle: {
    marginRight: LIST_MARGIN_HORIZONTAL,
  },
  listContainer: {
    flex: 1,
  },
  modalHeader: {
    paddingBottom: 0,
  },
  title: {
    paddingHorizontal: TITLE_PADDING,
    paddingTop: TITLE_PADDING,
  },
  titleSmall: {
    paddingBottom: TITLE_PADDING,
  },
});
